export type OnResize<Extra extends {} = {}> = (entry: ResizeObserverEntry, ctx: ElementContext<Extra>) => void;

type ElementContext<Extra extends {} = {}> = {
  cb?: (entry: ResizeObserverEntry, ctx: ElementContext<Extra>) => void;
} & Extra;

const observeResize = <T extends Element>(onResize?: OnResize) => {
  const elements = new Map<Element, ElementContext>();

  const observer = new ResizeObserver((entries) => {
    requestAnimationFrame(() => {
      entries.forEach((entry) => {
        const ctx = elements.get(entry.target as T)!;
        if (!entry.target.parentElement) {
          remove(entry.target as T);
          return;
        }
        onResize?.(entry, ctx);
        ctx?.cb?.(entry, ctx);
      });
    });
  });

  const getEntry = (item: T) => {
    const contentRect = item.getBoundingClientRect();
    const borderBoxSize = [{ inlineSize: contentRect.width, blockSize: contentRect.height }];
    const contentBoxSize = [{ inlineSize: contentRect.width, blockSize: contentRect.height }];
    const devicePixelContentBoxSize = [
      {
        inlineSize: contentRect.width * window.devicePixelRatio,
        blockSize: contentRect.height * window.devicePixelRatio,
      },
    ];
    const target = item;

    return {
      target,
      contentRect,
      borderBoxSize,
      contentBoxSize,
      devicePixelContentBoxSize,
    } satisfies ResizeObserverEntry;
  };

  const add = <Extra extends {} = {}>(item: T, ctx: ElementContext<Extra> = {} as Extra) => {
    const entry = getEntry(item);
    onResize?.(entry, ctx as ElementContext);
    ctx.cb?.(entry, ctx);
    elements.set(item, ctx as ElementContext);
    observer.observe(item);
  };

  const remove = (item: T) => {
    observer.unobserve(item);
    elements.delete(item);
  };

  const teardown = () => {
    observer.disconnect();
  };

  return {
    add,
    remove,
    teardown,
  } as const;
};

export const resizers = observeResize();
